import { Injectable, computed, inject, signal } from '@angular/core';

import { Store } from '@ngrx/store';

import {
  injectApplyFeedPostAlgorithmMutation,
  injectDetachFeedAlgorithmMutation,
  injectFeedAlgorithmsQuery,
  injectUpdateFeedAlgorithmParamsMutation,
} from 'src/app/api/queries/feed.queries';
import { AppState } from 'src/app/reducers';

import { ContentFilters } from '../models/core.models';
import { FeedAlgorithm } from '../models/algorithm.models';
import { getContentFilters, selectFeed } from '../reducers';

/**
 * The current feed's algorithms — the list, the detach/params writes and the
 * run-over-the-result-set call.
 *
 * **A root service because two of its four consumers are not components**:
 * `breadcrumb.service` (the algorithm badge) and `post-algorithm.service`. The
 * other two are `feed.component`'s sidebar-button gate and the algorithms panel.
 *
 * Keyed on the current feed, which is the only one the panel is ever opened for.
 */
@Injectable({ providedIn: 'root' })
export class FeedAlgorithmsService {
  private readonly store = inject(Store<AppState>);

  private readonly feed = this.store.selectSignal(selectFeed);
  private readonly contentFilters = this.store.selectSignal(getContentFilters);
  private readonly feedId = computed<string | null>(
    () => this.feed()?.id ?? null,
  );

  private readonly query = injectFeedAlgorithmsQuery(this.feedId);

  private readonly detachMutation = injectDetachFeedAlgorithmMutation();
  private readonly updateParamsMutation =
    injectUpdateFeedAlgorithmParamsMutation();
  private readonly applyMutation = injectApplyFeedPostAlgorithmMutation();

  /**
   * In-flight algorithm ids. Here rather than in the panel so they survive the
   * panel being closed and re-opened mid-request.
   */
  private readonly pending = signal<readonly number[]>([]);

  readonly algorithms = computed<FeedAlgorithm[]>(
    () => this.query.data() ?? [],
  );
  readonly attached = computed(() =>
    this.algorithms().filter((algorithm) => algorithm.is_selected),
  );
  readonly loading = computed(() => this.query.isFetching());
  readonly pendingIds = this.pending.asReadonly();

  /**
   * Re-fetch the list — the panel calls this on every open.
   *
   * **Load-bearing.** This service's query observer is permanent, so remounting
   * the panel fetches nothing on its own; without this call a newly scheduled
   * algorithm would only appear after a full page reload.
   */
  refresh(): void {
    this.query.refetch();
  }

  // ! There is no `attach()`: an algorithm is attached by **scheduling** it from
  // ! the Actions menu (WAP-1379), which is what `applyToFeedPosts` below does
  // ! and why it refetches on the scheduled branch.

  detach(
    feedId: string,
    algorithmId: number,
    scheduleId?: string | null,
  ): void {
    this.addPending([algorithmId]);

    this.detachMutation
      .mutateAsync({ feedId, algorithmId, scheduleId })
      .catch(() => undefined)
      .finally(() => this.removePending([algorithmId]));
  }

  updateParams(
    feedId: string,
    algorithmId: number,
    params: Record<string, any>,
    scheduleId?: string | null,
  ): void {
    this.addPending([algorithmId]);

    this.updateParamsMutation
      .mutateAsync({ feedId, algorithmId, params, scheduleId })
      .catch(() => undefined)
      .finally(() => this.removePending([algorithmId]));
  }

  /**
   * Run an algorithm over the feed's current result set. The content filters
   * default to the feed view's, from the store, because no caller inside a feed
   * has them to hand. `contentFilters` overrides them for a run triggered
   * outside a feed view - "Run as new" in the runs grid replays the original
   * run's persisted filters, and there is no feed-view state to read.
   *
   * **A scheduled run refetches the list; a plain run does not.** The backend
   * branches on `params.schedule`: true means it attaches the algorithm
   * **synchronously inside this request**, so once the promise resolves a
   * refetch is guaranteed to see it. A plain run attaches nothing, so refetching
   * would be a wasted GET.
   */
  applyToFeedPosts(
    feedId: string,
    algorithmId: number,
    algorithmParams: Record<string, any>,
    contentFilters?: ContentFilters,
  ): void {
    const isScheduled = !!algorithmParams?.['schedule'];

    this.applyMutation
      .mutateAsync({
        feedId,
        algorithmId,
        contentFilters: contentFilters ?? this.contentFilters(),
        algorithmParams,
      })
      .then(() => {
        // * Only if we are still on the feed the run belongs to — the user may
        // * have navigated away while it was in flight.
        if (isScheduled && this.feedId() === feedId) {
          this.refresh();
        }
      })
      .catch(() => undefined);
  }

  private addPending(ids: number[]): void {
    this.pending.update((current) => [...new Set([...current, ...ids])]);
  }

  private removePending(ids: number[]): void {
    const done = new Set(ids);
    this.pending.update((current) => current.filter((id) => !done.has(id)));
  }
}
